import React from 'react'
import axios from 'axios'
import {
  MdCalendarToday,
  MdCheckCircle,
  MdContentCopy,
  MdEmail,
  MdHome,
  MdKeyboardArrowDown,
  MdOpenInNew,
  MdPhone,
} from 'react-icons/md'
import {
  addDays,
  eachDayOfInterval,
  format,
  isValid,
  parseISO,
  startOfDay,
} from 'date-fns'
import { getRequestHeaders } from '../api'
import { baseUrl } from '../assets/Animations'
import { createAndOrganizeTeachersCalendarEventsForDisplayedPeriodOfTime } from '../Calendar/calendarFunctions'
import { normalizeOptimizerAvailability } from '../Scheduling2025/scheduleOptimizer'
import './student-profile.css'

const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
const DISPLAY_DAYS = ['Monday', 'Friday', 'Tuesday', 'Saturday', 'Wednesday', 'Sunday', 'Thursday']
const SCHOOL_NAMES = {
  mma: 'Mountainside Music Academy',
  lsma: 'Lakeside Music Academy',
  tma: 'Treeside Music Academy',
  tsma: 'Treeside Music Academy',
  mor: 'Morningside Music Academy',
  psma: 'Parkside Music Academy',
  tem: 'Tempe Music Academy',
  den: 'Denton Music Academy',
  tul: 'Tulsa Music Academy',
}

const text = (value) => String(value || '').trim()

const familyIdFrom = (family) => text(
  family?.familyId || family?.familyID || family?._id || family?.id
)

const familyNameFrom = (family) => text(
  family?.familyName || family?.FamilyName || family?.name
) || 'Family'

const schoolNameFrom = (family) => {
  const school = text(family?.school || family?.School).toLowerCase()
  return SCHOOL_NAMES[school] || text(family?.school || family?.School) || 'Music Academy'
}

const addressLabelFrom = (family) => {
  const address = family?.address || family?.Address || {}
  const street = address.addressLine1 || address.StreetAddress || family?.addressLine1
  const city = address.city || address.City || family?.city
  const state = address.state || address.State || family?.state
  const zip = address.zipcode || address.Zip || address.zip || family?.zipcode
  return [street, [city, state].filter(Boolean).join(', '), zip].filter(Boolean).join(' ')
}

const studentAge = (student) => {
  const value = student?.dob || student?.DOB || student?.dateOfBirth
  if (!value) return ''
  const birthDate = typeof value === 'string' ? parseISO(value) : new Date(value)
  if (!isValid(birthDate)) return ''
  const today = new Date()
  let age = today.getFullYear() - birthDate.getFullYear()
  const monthDifference = today.getMonth() - birthDate.getMonth()
  if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < birthDate.getDate())) age -= 1
  return age >= 0 ? age : ''
}

const studentInstruments = (student) => {
  const instruments = Array.isArray(student?.instrumentList)
    ? student.instrumentList
    : Array.isArray(student?.InstrumentList)
      ? student.InstrumentList
      : student?.instrument || student?.Instrument
        ? [student.instrument || student.Instrument]
        : []

  return instruments.map((instrument) => ({
    name: text(
      typeof instrument === 'string'
        ? instrument
        : instrument?.name || instrument?.Name || instrument?.parentInstrument
    ),
    length: Number(
      instrument?.lessonLength
      || instrument?.length
      || student?.lessonLength
      || student?.Length
      || 0
    ),
  })).filter((instrument) => instrument.name)
}

const minutesToTime = (minutes) => {
  if (!Number.isFinite(minutes)) return ''
  const hour24 = Math.floor(minutes / 60) % 24
  const minute = minutes % 60
  return `${hour24 % 12 || 12}:${String(minute).padStart(2, '0')}${hour24 >= 12 ? 'PM' : 'AM'}`
}

const availabilityLabel = (availability, day) => {
  const blocks = availability?.byDay?.[DAY_NAMES.indexOf(day)] || []
  if (blocks.length === 0) return 'Unavailable'
  return blocks.map(({ start, end }) => `${minutesToTime(start)} - ${minutesToTime(end)}`).join(', ')
}

const normalizeCalendarEvents = (events) => (
  (Array.isArray(events) ? events : [])
    .filter((event) => event && !event.isPendingOffer && !event.isAvailabilityPlaceholder)
    .map((event) => ({
      ...event,
      recurring: event.recurring && typeof event.recurring === 'object'
        ? event.recurring
        : { isOn: false, frequency: 'none', interval: 0 },
      recurringEndDate: event.recurringEndDate || '',
    }))
)

const upcomingLessonFrom = (events) => {
  const today = startOfDay(new Date())
  const days = eachDayOfInterval({ start: today, end: addDays(today, 90) })
  let calendar = {}
  try {
    createAndOrganizeTeachersCalendarEventsForDisplayedPeriodOfTime(
      days,
      normalizeCalendarEvents(events),
      (result) => { calendar = result || {} }
    )
  } catch (error) {
    console.error('Unable to organize the family calendar:', error)
  }

  for (const day of days) {
    const lessons = (calendar[format(day, 'yyyy-MM-dd')]?.events || [])
      .map((event) => ({ event, date: new Date(event.dateTime) }))
      .filter(({ date }) => isValid(date) && date >= new Date())
      .sort((left, right) => left.date - right.date)
    if (lessons.length > 0) return lessons[0]
  }
  return null
}

const CopyRow = ({ icon: Icon, label, value }) => {
  const [copied, setCopied] = React.useState(false)
  if (!value) return null

  const copy = async () => {
    try {
      await navigator.clipboard?.writeText(value)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1200)
    } catch (error) {
      console.error(`Unable to copy ${label}:`, error)
    }
  }

  return (
    <div className='atharo-student__contact-row'>
      <Icon aria-hidden='true' />
      <span>{value}</span>
      <button type='button' onClick={copy} aria-label={`Copy ${label}`} title={copied ? 'Copied' : `Copy ${label}`}>
        {copied ? <MdCheckCircle /> : <MdContentCopy />}
      </button>
    </div>
  )
}

const CopyButton = ({ label, value }) => {
  const [copied, setCopied] = React.useState(false)
  const copy = async () => {
    if (!value) return
    try {
      await navigator.clipboard?.writeText(value)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1200)
    } catch (error) {
      console.error(`Unable to copy ${label}:`, error)
    }
  }

  return (
    <button type='button' className='atharo-student__copy-button' onClick={copy} disabled={!value}>
      <span>{copied ? 'Copied' : label}</span>
      <MdContentCopy aria-hidden='true' />
    </button>
  )
}

const ReadOnlyFlag = ({ label, value }) => (
  <div className='atharo-student__flag'>
    <span className={`atharo-student__switch ${value ? 'is-on' : ''}`} aria-hidden='true'><i /></span>
    <span>{label}</span>
  </div>
)

const schoolSubdomainFrom = (school) => ({
  mma: 'mountainside',
  lsma: 'lakeside',
  tma: 'treeside',
  mor: 'morningside',
  psma: 'parkside',
  den: 'denton',
  tem: 'tempe',
  tul: 'tulsa',
}[text(school).toLowerCase()] || 'app')

const referralHostFrom = (school) => ({
  mma: 'www.mountainsidemusicacademy.com',
  lsma: 'www.lakesidemusicacademy.com',
  tma: 'www.treesidemusicacademy.com',
  mor: 'www.morningsidemusicacademy.com',
  psma: 'www.parksidemusicacademy.com',
  den: 'www.atharomusic.com/denton-music-academy',
  tem: 'www.atharomusic.com/tempe-music-academy',
  tul: 'www.atharomusic.com/tulsa-music-academy',
}[text(school).toLowerCase()] || 'app.atharo.app')

const StudentProfile = ({ family, onFamilyLoaded, onViewFullProfile }) => {
  const [profileFamily, setProfileFamily] = React.useState(family || {})
  const [policies, setPolicies] = React.useState(null)
  const [calendarEvents, setCalendarEvents] = React.useState([])
  const familyId = familyIdFrom(family)
  const familyRef = React.useRef(family || {})
  const onFamilyLoadedRef = React.useRef(onFamilyLoaded)

  React.useEffect(() => {
    familyRef.current = family || {}
    setProfileFamily(family || {})
  }, [family])

  React.useEffect(() => {
    onFamilyLoadedRef.current = onFamilyLoaded
  }, [onFamilyLoaded])

  React.useEffect(() => {
    if (!familyId) return undefined
    let active = true

    Promise.allSettled([
      axios.get(`${baseUrl}/school-management/${encodeURIComponent(familyId)}`, getRequestHeaders()),
      axios.get(`${baseUrl}/policies-signed/${encodeURIComponent(familyId)}`, getRequestHeaders()),
      axios.get(`${baseUrl}/calendar/family/${encodeURIComponent(familyId)}`, getRequestHeaders()),
    ]).then(([familyRequest, policiesRequest, calendarRequest]) => {
      if (!active) return
      if (familyRequest.status === 'fulfilled' && familyRequest.value?.data) {
        const loadedFamily = { ...familyRef.current, ...familyRequest.value.data }
        setProfileFamily(loadedFamily)
        onFamilyLoadedRef.current?.(loadedFamily)
      }
      if (policiesRequest.status === 'fulfilled') setPolicies(policiesRequest.value?.data || null)
      if (calendarRequest.status === 'fulfilled') {
        const calendarPayload = calendarRequest.value?.data
        setCalendarEvents(
          Array.isArray(calendarPayload)
            ? calendarPayload
            : Array.isArray(calendarPayload?.events)
              ? calendarPayload.events
              : []
        )
      }
    })

    return () => { active = false }
  }, [familyId])

  const school = text(profileFamily?.school || profileFamily?.School)
  const students = Array.isArray(profileFamily?.students) ? profileFamily.students : []
  const availability = React.useMemo(
    () => normalizeOptimizerAvailability(
      profileFamily?.initialAvailability
      || profileFamily?.availability
      || profileFamily?.AvailabilityDetails
      || {}
    ),
    [profileFamily]
  )
  const upcomingLesson = React.useMemo(
    () => upcomingLessonFrom(calendarEvents),
    [calendarEvents]
  )
  const address = addressLabelFrom(profileFamily)
  const status = text(profileFamily?.familyStatus || profileFamily?.Status) || 'Not recorded'
  const signedPolicies = Boolean(policies?.signature || profileFamily?.policiesSigned)
  const submittedDate = new Date(profileFamily?.onboardDate || profileFamily?.submittedDate || '')
  const studentInfoUrl = familyId
    ? `https://${schoolSubdomainFrom(school)}.atharo.app/new-student/${familyId}`
    : ''
  const policiesUrl = familyId ? `https://app.atharo.app/student-policies/${familyId}` : ''
  const referralUrl = familyId ? `https://${referralHostFrom(school)}/referral/${familyId}` : ''

  return (
    <div className='atharo-student'>
      <div className='atharo-student__scroll-area'>
        <section className='atharo-student__hero'>
          <div>
            <small>{schoolNameFrom(profileFamily)}</small>
            <h2>{familyNameFrom(profileFamily)}</h2>
            {upcomingLesson && (
              <span className='atharo-student__next-lesson'>
                <MdCalendarToday aria-hidden='true' /> {format(upcomingLesson.date, 'EEE')} • {format(upcomingLesson.date, 'h:mm a')}
              </span>
            )}
          </div>
          <span className={`atharo-student__status is-${status.toLowerCase().replace(/\s+/g, '-')}`}>
            {status} <MdKeyboardArrowDown aria-hidden='true' />
          </span>
        </section>

        <section className='atharo-student__section'>
          <h3>Contact</h3>
          <CopyRow icon={MdEmail} label='email address' value={text(profileFamily?.primaryEmail || profileFamily?.Email)} />
          <CopyRow icon={MdPhone} label='phone number' value={text(profileFamily?.primaryCellphone || profileFamily?.PhoneNumber)} />
          <CopyRow icon={MdHome} label='home address' value={address} />
        </section>

        <section className='atharo-student__links' aria-label='Family links'>
          <CopyButton label='Student Info' value={studentInfoUrl} />
          <CopyButton label='Policies' value={policiesUrl} />
          <CopyButton label='Abc' value={text(profileFamily?.abcLink || profileFamily?.abc)} />
          <CopyButton label='Referral Link' value={referralUrl} />
        </section>

        <section className='atharo-student__flags' aria-label='Family account flags'>
          <ReadOnlyFlag label='Needs Attention' value={profileFamily?.moreAttention} />
          <ReadOnlyFlag label='Auto-Pay' value={profileFamily?.autoPay} />
          <ReadOnlyFlag label='Left Review' value={profileFamily?.leftReview} />
          <ReadOnlyFlag label='Sibling Discount' value={profileFamily?.siblingDiscount} />
        </section>

        <section className='atharo-student__section'>
          <h3>Students</h3>
          <div className='atharo-student__student-list'>
            {students.map((student, index) => {
              const age = studentAge(student)
              const instruments = studentInstruments(student)
              return (
                <div
                  className={`atharo-student__student-row ${instruments.length > 2 ? 'atharo-student__student-row--multi-instrument' : ''}`}
                  key={student?.studentId || `${student?.studentName}-${index}`}
                >
                  <strong>
                    {text(student?.studentName || student?.name) || `Student ${index + 1}`}
                    {age !== '' ? ` (${age})` : ''}
                  </strong>
                  <span className='atharo-student__student-instruments'>
                    {instruments.map((instrument) => (
                      <span key={`${instrument.name}-${instrument.length}`}>
                        <b>{instrument.name}</b>
                        {instrument.length > 0 && <small>{instrument.length} min</small>}
                      </span>
                    ))}
                  </span>
                </div>
              )
            })}
            {students.length === 0 && <div className='atharo-student__empty'>No students are listed for this family.</div>}
          </div>
        </section>

        <section className='atharo-student__submission'>
          <span>
            <MdCalendarToday aria-hidden='true' />
            <span>Form Submitted:{isValid(submittedDate) && <strong>{format(submittedDate, 'MM/dd/yyyy')}</strong>}</span>
          </span>
          <span className={signedPolicies ? 'is-signed' : ''}>
            <MdCheckCircle aria-hidden='true' /> {signedPolicies ? 'Policies Signed' : 'Policies Pending'}
          </span>
        </section>

        <section className='atharo-student__section atharo-student__availability-section'>
          <h3>Availability</h3>
          <div className='atharo-student__availability-grid'>
            {DISPLAY_DAYS.map((day) => {
              const label = availabilityLabel(availability, day)
              return (
                <div className={`atharo-student__availability ${label === 'Unavailable' ? 'is-unavailable' : ''}`} key={day}>
                  <span>{day.slice(0, 3).toUpperCase()}</span>
                  <strong>{label}</strong>
                </div>
              )
            })}
          </div>
        </section>
      </div>

      <footer className='atharo-student__footer'>
        <button type='button' onClick={() => onViewFullProfile?.(profileFamily)}>
          View Full Profile <MdOpenInNew aria-hidden='true' />
        </button>
      </footer>
    </div>
  )
}

export default StudentProfile
